import React, { FC, Fragment } from 'react';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, TableSortLabel } from '@mui/material';
import { OrderBy } from 'models/search-result';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import ClinicItem from './clinic-item.component';
import { Clinic } from 'models/clinic';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';

type ClinicListProps = {
    filteredClinics: Clinic[];
    onFetchMore: () => void;
    onSetOrderOnField: (field: string) => void;
    orderBy: OrderBy;
    onEditClinicClick: (clinic: Clinic) => void;
    onHHSyncClick?: (clinic: Clinic) => void;
    onAddChildClinicClick?: (clinic: Clinic) => void;
};
const ClinicList: FC<ClinicListProps> = ({
    filteredClinics,
    onFetchMore,
    onSetOrderOnField,
    orderBy,
    onEditClinicClick,
    onHHSyncClick,
    onAddChildClinicClick,
}: ClinicListProps) => {
    const { t: translate } = useTranslation(['common', 'organization', 'organization-numbers']);

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(onFetchMore, filteredClinics?.length || 0);

    return (
        <Fragment>
            <TableContainer component={Paper}>
                <InfiniteScroll
                    dataLength={filteredClinics ? filteredClinics.length : 0}
                    next={onFetchMore}
                    throttleTimeInMs={75}
                    hasMore={true}
                    loader={undefined}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">{translate('common:STATUS')}</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        onClick={() => onSetOrderOnField('name')}
                                        active={orderBy.orderBy === 'name'}
                                        direction={orderBy.orderDirection}
                                    >
                                        {translate('common:NAME')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        onClick={() => onSetOrderOnField('organizationNumber')}
                                        active={orderBy.orderBy === 'organizationNumber'}
                                        direction={orderBy.orderDirection}
                                    >
                                        {translate('organization-numbers:SE_ORGANIZATION_NUMBER')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>{translate('common:ORGANIZATION_TYPE')}</TableCell>
                                <TableCell>{translate('organization:ADDRESS:ADDRESS')}</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        onClick={() => onSetOrderOnField('created.on')}
                                        active={orderBy.orderBy === 'created.on'}
                                        direction={orderBy.orderDirection}
                                    >
                                        {translate('common:CREATED')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>{translate('organization:MAIN_ORGANIZATION')}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredClinics ? (
                                <Fragment>
                                    {filteredClinics.map((clinic: Clinic, index: number) => (
                                        <ClinicItem
                                            key={index}
                                            clinic={clinic}
                                            onEditClinicClick={onEditClinicClick}
                                            onHHSyncClick={onHHSyncClick}
                                            onAddChildClinicClick={onAddChildClinicClick}
                                        />
                                    ))}
                                </Fragment>
                            ) : undefined}
                        </TableBody>
                    </Table>
                </InfiniteScroll>
            </TableContainer>
        </Fragment>
    );
};

export default ClinicList;
